import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/home/CTASection';
import { companyInfo } from '../data/mock';

const TrustPage = () => {
  const accreditations = [
    {
      id: "netcc",
      title: "NETCC Approved Seller",
      description: "We're authorised under the New Energy Tech Consumer Code, an ACCC-approved program ensuring the highest consumer protection standards for solar, batteries, EV chargers and more.",
      icon: "/netcc-approved-seller.png",
      isImage: true,
      benefits: [
        "Ethical sales with no pressure tactics or misleading claims",
        "Clear, transparent quotes with no hidden costs",
        "Quality installation and product standards",
        "Complaints handled promptly with independent oversight",
        "Ongoing compliance audits and monitoring"
      ],
      link: "https://www.newenergytech.org.au/find-an-approved-seller"
    },
    {
      id: "solar-vic",
      title: "Solar Victoria Approved Retailer",
      description: "Authorised to deliver Solar Victoria rebate programs under the Solar Homes Program, ensuring you can access government incentives with confidence.",
      icon: null,
      isImage: false,
      benefits: [
        "Access to Solar Homes Program rebates (up to $1,400+)",
        "Interest-free loan eligibility",
        "Compliance with Victorian standards",
        "Quality-assured installations"
      ],
      link: "https://www.solar.vic.gov.au/find-an-authorised-retailer"
    },
    {
      id: "installers",
      title: "Accredited Installers",
      description: "All our installations are performed by accredited professionals who meet rigorous training and safety standards required for STC eligibility.",
      icon: null,
      isImage: false,
      benefits: [
        "Fully qualified and licensed technicians",
        "Compliant with Australian Standards",
        "Eligible for Small-scale Technology Certificates (STCs)",
        "Professional workmanship guarantee"
      ],
      link: null
    },
    {
      id: "standards",
      title: "Australian Standards Compliant",
      description: "All systems we install meet or exceed Australian safety and quality standards, including AS/NZS 5033 for solar installations.",
      icon: null,
      isImage: false,
      benefits: [
        "AS/NZS 5033 compliant solar installations",
        "DNSP grid connection requirements met",
        "Electrical safety standards adhered to",
        "Proper documentation and certification"
      ],
      link: null
    }
  ];

  const policies = [
    {
      title: "Customer Care Policy",
      description: "We're committed to treating every customer fairly and transparently. Our customer care policy outlines your rights and our responsibilities."
    },
    {
      title: "Warranty Management",
      description: "All warranties are honoured and managed locally. If something goes wrong, you deal directly with us — not overseas call centres."
    },
    {
      title: "After-Sales Support",
      description: "Our relationship doesn't end at installation. We provide ongoing support for the life of your system."
    },
    {
      title: "Complaints Resolution",
      description: "If you're ever unhappy, we have a clear process to address your concerns promptly and fairly."
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 hero-gradient">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-4">
                Trust & Compliance
              </span>
              <h1 className="heading-1 mb-6">
                Your Peace of Mind is Our Priority
              </h1>
              <p className="body-large">
                We maintain the highest industry standards because you deserve to 
                work with a company you can trust completely.
              </p>
            </div>
          </div>
        </section>

        {/* NETCC Highlight Banner */}
        <section className="py-8 bg-gradient-to-r from-[#0B4A6F] to-[#0D5A84]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-6 text-white">
              <div className="flex-shrink-0 bg-white rounded-xl p-4">
                <img 
                  src="/netcc-approved-seller.png" 
                  alt="NETCC Approved Seller" 
                  className="h-16 w-auto"
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold mb-2">New Energy Tech Consumer Code Approved Seller</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  We're one of over 2,000 Australian retailers committed to the ACCC-authorised consumer protection code. 
                  This means ethical sales practices, transparent pricing, quality installations, and reliable after-sales support.
                </p>
              </div>
              <a 
                href="https://www.newenergytech.org.au/find-an-approved-seller" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
              >
                Verify on NETCC
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </section>

        {/* Accreditations Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="heading-2 mb-5">Our Accreditations</h2>
              <p className="body-large">
                These aren't just badges — they represent our commitment to quality, 
                compliance, and consumer protection.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {accreditations.map((acc) => (
                <div 
                  key={acc.id}
                  className="bg-white rounded-2xl border border-black/5 p-6 md:p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-5 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-[#0B4A6F]/10 flex items-center justify-center flex-shrink-0">
                      {acc.isImage ? (
                        <img src={acc.icon} alt={acc.title} className="w-8 h-8" />
                      ) : (
                        <svg className="w-7 h-7 text-[#3D5A47]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                        </svg>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-[#3D5A47] mb-2">
                        {acc.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {acc.description}
                      </p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-4">
                    {acc.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {acc.link && (
                    <a 
                      href={acc.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#3D5A47] text-sm font-medium hover:text-[#B5D43B] transition-colors"
                    >
                      Verify this accreditation
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What This Means Section */}
        <section className="py-16 section-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <h2 className="heading-2 mb-6">What This Means for You</h2>
                <p className="body-large mb-8">
                  When you choose Supa Energy Hub, you're protected by multiple 
                  layers of consumer safeguards.
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span className="text-[#3D5A47]">
                      <strong>Fair Contracts:</strong> Clear terms without hidden clauses or unfair conditions
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span className="text-[#3D5A47]">
                      <strong>Quality Assurance:</strong> Every installation meets or exceeds Australian Standards
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span className="text-[#3D5A47]">
                      <strong>Dispute Resolution:</strong> Access to independent dispute resolution if needed
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span className="text-[#3D5A47]">
                      <strong>Rebate Eligibility:</strong> Authorised to deliver government rebate programs
                    </span>
                  </li>
                </ul>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-black/5 text-center">
                  <img 
                    src="/netcc-approved-seller.png" 
                    alt="NETCC Approved Seller" 
                    className="h-10 w-auto mx-auto mb-3"
                  />
                </div>
                <div className="bg-white p-6 rounded-2xl border border-black/5 text-center">
                  <svg className="w-10 h-10 text-[#1E40AF] mx-auto mb-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  <p className="font-semibold text-[#3D5A47]">Solar Victoria</p>
                  <p className="text-xs text-gray-500">Approved</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-black/5 text-center">
                  <svg className="w-10 h-10 text-[#059669] mx-auto mb-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  <p className="font-semibold text-[#3D5A47]">Accredited</p>
                  <p className="text-xs text-gray-500">Installers</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-black/5 text-center">
                  <svg className="w-10 h-10 text-[#DC2626] mx-auto mb-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  <p className="font-semibold text-[#3D5A47]">Australian</p>
                  <p className="text-xs text-gray-500">Standards</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Policies Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="heading-2 mb-5">Our Customer Commitments</h2>
              <p className="body-large">
                These policies guide how we work with every customer.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {policies.map((policy, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl border border-black/5 p-6 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{policy.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{policy.description}</p>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">Have questions about our policies?</p>
              <a href={`tel:${companyInfo.phone}`} className="inline-flex items-center gap-2 text-[#3D5A47] font-medium hover:text-[#B5D43B] hover:underline transition-colors">
                <Phone className="w-4 h-4" />
                Call us on {companyInfo.phone}
              </a>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default TrustPage;
